import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiPlay, FiPause, FiSquare, FiClock, FiActivity, FiTrendingUp, FiPlus, FiMinus, FiMapPin } = FiIcons;

const WorkoutTimer = ({ timerData, workoutProgram }) => {
  const { t } = useLanguage();

  if (!workoutProgram || !timerData) return null;

  const {
    isActive, isPaused, currentPhase, timeRemaining, progressPercentage,
    currentSpeed, distanceTraveled, handleStart, handlePause, handleStop, adjustSpeed
  } = timerData;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.floor(meters)} m`;
  };

  const getPhaseName = (name) => {
    switch(name) {
      case 'warmup': return t('warmup');
      case 'intense': return t('intense');
      case 'cooldown': return t('cooldown');
      default: return name;
    }
  };

  const getPhaseColor = (phaseName) => {
    switch (phaseName) {
      case 'warmup': return 'bg-amber-500';
      case 'intense': return 'bg-rose-500';
      case 'cooldown': return 'bg-blue-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-8 h-full flex flex-col relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-8 relative z-10">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Timer</h2>
          <div className="flex items-center gap-4 text-slate-500 text-sm mt-1">
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiClock} />
              <span>{t('total_time')}: {workoutProgram.total_time_min} min</span>
            </div>
          </div>
        </div>
        {currentPhase && (
          <div className={`px-4 py-2 rounded-full text-white text-sm font-bold shadow-lg capitalize ${getPhaseColor(currentPhase.name)}`}>
            {getPhaseName(currentPhase.name)}
          </div>
        )}
      </div>

      {/* Main Display */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10">
        {/* Time */}
        <div className="relative mb-6 text-center">
          <div className="text-8xl font-bold text-slate-900 tracking-tighter font-variant-numeric tabular-nums leading-none">
            {formatTime(timeRemaining)}
          </div>
          <div className="text-slate-400 font-medium uppercase tracking-widest text-sm mt-2">
            {t('time_remaining')}
          </div>
        </div>

        {/* LIVE METRICS GRID */}
        <div className="grid grid-cols-2 gap-4 w-full max-w-sm mb-8">
          {/* Speed Control */}
          <div className={`bg-slate-50 rounded-2xl p-4 border ${isActive ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-slate-500 uppercase">{t('speed')}</span>
              <SafeIcon icon={FiActivity} className="text-blue-500" />
            </div>
            <div className="flex items-center justify-between">
              <button 
                onClick={() => isActive && adjustSpeed(-0.1)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 active:scale-95 disabled:opacity-50"
                disabled={!isActive}
              >
                <SafeIcon icon={FiMinus} size={14} />
              </button>
              <span className="text-2xl font-bold text-slate-800 tabular-nums">
                {currentSpeed.toFixed(1)}
              </span>
              <button 
                onClick={() => isActive && adjustSpeed(0.1)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 active:scale-95 disabled:opacity-50"
                disabled={!isActive}
              >
                <SafeIcon icon={FiPlus} size={14} />
              </button>
            </div>
            <div className="text-center text-xs text-slate-400 mt-1">km/h</div>
          </div>

          {/* Distance Live */}
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex flex-col justify-between">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-slate-500 uppercase">{t('distance')}</span>
              <SafeIcon icon={FiMapPin} className="text-emerald-500" />
            </div>
            <div className="text-center">
              <span className="text-2xl font-bold text-slate-800 tabular-nums">
                {formatDistance(distanceTraveled)}
              </span>
            </div>
            <div className="text-center text-xs text-slate-400 mt-1">{t('covered')}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex gap-4 w-full max-w-xs">
          {!isActive ? (
            <button 
              onClick={handleStart}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <SafeIcon icon={FiPlay} className="text-xl" /> {t('start')}
            </button>
          ) : (
            <>
              <button 
                onClick={handlePause}
                className={`flex-1 py-4 rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95 
                  ${isPaused ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/30' : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/30'}`}
              >
                <SafeIcon icon={isPaused ? FiPlay : FiPause} className="text-xl" /> 
                {isPaused ? t('resume') : t('pause')}
              </button>
              <button 
                onClick={handleStop}
                className="w-16 bg-red-100 hover:bg-red-200 text-red-600 rounded-2xl flex items-center justify-center transition-all active:scale-95"
              >
                <SafeIcon icon={FiSquare} className="text-xl" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Footer Progress */}
      <div className="mt-auto pt-8 relative z-10">
        <div className="flex justify-between text-xs font-bold text-slate-500 mb-2">
          <span>{t('progress').toUpperCase()}</span>
          <span>{Math.round(progressPercentage)}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-blue-600 rounded-full"
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkoutTimer;